import { formatMoney } from '@/lib/utils';
import type { Expense, ExpenseSplit } from '@/lib/types';

export type UnpaidExpenseLike = {
  id: number;
  name: string;
  amount_cents: number;
  category: string;
  purchase_date: string;
  purchaser_user_id?: number | null;
  splits?: Pick<ExpenseSplit, 'split_for_user_id' | 'amount_cents'>[];
  linked_payments?: { amount_cents: number }[];
  is_fully_paid_back?: boolean;
};

export function getExpenseRemainingCents(
  expense: UnpaidExpenseLike,
  currentUserId?: number | null
): number {
  if (!currentUserId) return 0;

  const splits = expense.splits ?? [];
  const mySplit = splits.find((s) => s.split_for_user_id === currentUserId);
  const theirSplit = splits.find((s) => s.split_for_user_id !== currentUserId);
  const purchaserIsYou = expense.purchaser_user_id === currentUserId;
  const reimbursableCents = purchaserIsYou
    ? (theirSplit?.amount_cents ?? 0)
    : (mySplit?.amount_cents ?? 0);
  const totalPaidBackCents = (expense.linked_payments ?? []).reduce(
    (sum, p) => sum + p.amount_cents,
    0
  );

  return Math.max(0, reimbursableCents - totalPaidBackCents);
}

export type ExpenseSettlementInfo = {
  isFullyPaidBack: boolean;
  reimbursableCents: number;
  totalPaidBackCents: number;
  remainingCents: number;
  hasPartialPayment: boolean;
  paymentProgress: number;
  settlementStatusLine: string;
  showRecordPayment: boolean;
};

export function getExpenseSettlementInfo(
  expense: Expense | undefined,
  currentUserId: number | undefined,
  hasDispute: boolean,
  getDisplayName: (userId: number | null | undefined) => string
): ExpenseSettlementInfo {
  const isFullyPaidBack = expense?.is_fully_paid_back ?? false;
  const splits = expense?.splits ?? [];
  const mySplit = splits.find((s) => s.split_for_user_id === currentUserId);
  const theirSplit = splits.find((s) => s.split_for_user_id !== currentUserId);
  const purchaserIsYou = expense?.purchaser_user_id === currentUserId;
  const totalPaidBackCents = (expense?.linked_payments ?? []).reduce(
    (sum, p) => sum + p.amount_cents,
    0
  );
  const reimbursableCents = purchaserIsYou
    ? (theirSplit?.amount_cents ?? 0)
    : (mySplit?.amount_cents ?? 0);
  const remainingCents = Math.max(0, reimbursableCents - totalPaidBackCents);
  const hasPartialPayment =
    totalPaidBackCents > 0 && !isFullyPaidBack && reimbursableCents > 0;
  const paymentProgress =
    reimbursableCents > 0
      ? Math.min(1, totalPaidBackCents / reimbursableCents)
      : 0;

  const purchaserName = expense?.purchaser_user_id
    ? getDisplayName(expense.purchaser_user_id)
    : null;

  let settlementStatusLine = '';
  if (isFullyPaidBack) {
    settlementStatusLine = 'This expense has been fully repaid';
  } else if (purchaserName) {
    const otherMemberId = splits.find(
      (s) => s.split_for_user_id !== currentUserId
    )?.split_for_user_id;
    settlementStatusLine = purchaserIsYou
      ? `${getDisplayName(otherMemberId)} owes ${formatMoney(remainingCents)}`
      : `You owe ${formatMoney(remainingCents)}`;
  } else if (remainingCents > 0) {
    settlementStatusLine = `${formatMoney(remainingCents)} outstanding`;
  }

  const showRecordPayment =
    !isFullyPaidBack && !hasDispute && remainingCents > 0;

  return {
    isFullyPaidBack,
    reimbursableCents,
    totalPaidBackCents,
    remainingCents,
    hasPartialPayment,
    paymentProgress,
    settlementStatusLine,
    showRecordPayment,
  };
}
